import i18n from 'i18next';
import { isAndroidOrHarmony, isMobile, PlatformUtil } from 'utils/platform';
import { isNullish } from 'utils/nullish';
import { ExceptionTypeInt, ExceptionTypeName } from 'utils/constants/exception-type-int';
import { ze } from 'utils/zhEn';

/** 异常上报的大类（联网、崩溃、卡顿、错误、OOM等） */
export const ExceptionCategory = Object.freeze({
  /** 联网 */
  ACCESS: 'ACCESS',
  /** 崩溃 */
  CRASH: 'CRASH',
  /** ANR */
  ANR: 'ANR',
  /** 错误 */
  ERROR: 'ERROR',
  /** OOM */
  OOM: 'OOM',
  /** 王者需求，新增的卡顿大类 */
  JANK: 'JANK',
  /** 王者需求，新增的卡死大类 */
  HANG: 'HANG',
  /** MetricKit 上报拆出的崩溃大类（iOS-only） */
  METRICKIT_CRASH: 'METRICKIT_CRASH',
  /** MetricKit CPU 异常诊断大类（iOS-only） */
  METRICKIT_CPU: 'METRICKIT_CPU',
  /** MetricKit 磁盘写入异常诊断大类（iOS-only）；CK 侧 exceptionCategory 为 metrickit_disk */
  METRICKIT_DISK: 'METRICKIT_DISK',
  AEXIT_CRASH: 'AEXIT_CRASH',
  /** ApplicationExitInfo ANR（落地 161 / 底表 61），与 AEXIT_CRASH 同源不同 type */
  AEXIT_ANR: 'AEXIT_ANR',
  /**
   * （仅支持ANR的端支持，例如安卓、鸿蒙）莉莉丝需求，“崩溃+ANR“合并的新指标。（界面上直接取代原来的崩溃指标，可以通过下拉切换原崩溃指标）
   * --story=127642761 【CS】【莉莉丝】大盘崩溃统计新增崩溃+ANR合并模式
   * */
  VITAL: 'VITAL',
  /** 未知 */
  UNKNOWN: 'UNKNOWN',
});

export const ExceptionCategoryUtil = (function() {
  /** 将各种混乱的异常大类文本转换成统一的ExceptionCategory */
  function unify(chaosCategory) {
    if (!chaosCategory) {
      return ExceptionCategory.UNKNOWN;
    }
    if (ExceptionCategory[chaosCategory]) {
      return ExceptionCategory[chaosCategory];
    }
    switch (String(chaosCategory).toLowerCase()) {
      case 'access':
      case '联网': {
        return ExceptionCategory.ACCESS;
      }
      case 'crash':
      case 'crashes':
      case '崩溃': {
        return ExceptionCategory.CRASH;
      }
      case 'anr':
      case 'block':
      case 'blocks':
      case '卡顿': {
        return ExceptionCategory.ANR;
      }
      case 'error':
      case 'errors':
      case '错误': {
        return ExceptionCategory.ERROR;
      }
      case 'oom': {
        return ExceptionCategory.OOM;
      }
      case 'jank':
      case 'janks': {
        return ExceptionCategory.JANK;
      }
      case 'hang':
      case 'hangs': {
        return ExceptionCategory.HANG;
      }
      case 'metrickit_crash':
      case 'metrickit-crash': {
        return ExceptionCategory.METRICKIT_CRASH;
      }
      case 'metrickit_cpu':
      case 'metrickit-cpu': {
        return ExceptionCategory.METRICKIT_CPU;
      }
      case 'metrickit_disk':
      case 'metrickit-disk':
        return ExceptionCategory.METRICKIT_DISK;
      case 'metrickit_disc':
      case 'metrickit-disc':
        return ExceptionCategory.METRICKIT_DISK;
      case 'aexit_crash':
      case 'aexit-crash': {
        return ExceptionCategory.AEXIT_CRASH;
      }
      case 'aexit_anr':
      case 'aexit-anr': {
        return ExceptionCategory.AEXIT_ANR;
      }
      case 'vital': {
        return ExceptionCategory.VITAL;
      }
      default: {
        return ExceptionCategory.UNKNOWN;
      }
    }
  }

  /**
   * 将各种混乱的异常大类文本转换成统一的i18n文本
   * @param chaosCategory {string}
   * @param platformId {number}
   * @return {string}
   */
  function toI18n(chaosCategory, platformId) {
    if (!platformId) {
      console.error('toI18n, no platformId provided.');
    }
    const category = unify(chaosCategory);
    switch (category) {
      case ExceptionCategory.ACCESS: {
        return i18n.t('common.access');
      }
      case ExceptionCategory.CRASH: {
        return i18n.t('common.crash');
      }
      case ExceptionCategory.ANR: {
        return i18n.t('common.anr');
      }
      case ExceptionCategory.ERROR: {
        return i18n.t('common.error');
      }
      case ExceptionCategory.OOM: {
        return i18n.t('common.oom');
      }
      case ExceptionCategory.JANK: {
        return i18n.t('common.jank');
      }
      case ExceptionCategory.HANG: {
        return i18n.t('common.hang');
      }
      case ExceptionCategory.METRICKIT_CRASH: {
        return ze('MetricKit崩溃', 'MetricKit Crash');
      }
      case ExceptionCategory.METRICKIT_CPU: {
        return ze('MetricKit CPU异常', 'MetricKit CPU Exception');
      }
      case ExceptionCategory.METRICKIT_DISK: {
        return ze('MetricKit磁盘写入异常', 'MetricKit Disk Write Exception');
      }
      case ExceptionCategory.AEXIT_CRASH: {
        return ze('应用退出信息崩溃', 'ApplicationExitInfo Crash');
      }
      case ExceptionCategory.AEXIT_ANR: {
        return ze('应用退出信息ANR', 'ApplicationExitInfo ANR');
      }
      case ExceptionCategory.VITAL: {
        return ze('合并崩溃', 'Merged Crash');
      }
      default: {
        return 'Unknown';
      }
    }
  }

  function fromExceptionType(exceptionType) {
    if (!exceptionType) {
      return ExceptionCategory.UNKNOWN;
    }
    if (exceptionType === 'Crash' || exceptionType === 'Native' || exceptionType === 'ExtensionCrash') {
      return ExceptionCategory.CRASH;
    } else if (exceptionType === 'ANR') {
      return ExceptionCategory.ANR;
    } else if (exceptionType === 'JankRaw' || exceptionType === 'JankParsed') {
      return ExceptionCategory.JANK;
    } else if (exceptionType === ExceptionTypeName.Hang) {
      return ExceptionCategory.HANG;
    } else if (exceptionType === 'MetricKitCrash') {
      return ExceptionCategory.METRICKIT_CRASH;
    } else if (exceptionType === 'MetricKitCpu') {
      return ExceptionCategory.METRICKIT_CPU;
    } else if (exceptionType === 'MetricKitDisk' || exceptionType === 'MetricKitDisc') {
      return ExceptionCategory.METRICKIT_DISK;
    } else if (exceptionType === ExceptionTypeName.JankV2) {
      // 王者 ThreadMonitor 内部定制：JankV2（协议 145 / ES 45）走错误大类，不新建 ExceptionCategory
      return ExceptionCategory.ERROR;
    } else if (exceptionType === 'AexitCrash') {
      return ExceptionCategory.AEXIT_CRASH;
    } else if (exceptionType === 'AexitAnr') {
      return ExceptionCategory.AEXIT_ANR;
    } else {
      return ExceptionCategory.ERROR;
    }
  }

  function fromExceptionTypeInt(exceptionTypeInt) {
    if (isNullish(exceptionTypeInt)) {
      return ExceptionCategory.UNKNOWN;
    }
    exceptionTypeInt = Number(exceptionTypeInt);
    switch (exceptionTypeInt) {
      case ExceptionTypeInt.Crash:
      case ExceptionTypeInt.Native:
      case ExceptionTypeInt.ExtensionCrash: {
        return ExceptionCategory.CRASH;
      }
      case ExceptionTypeInt.ANR: {
        return ExceptionCategory.ANR;
      }
      case ExceptionTypeInt.JankRaw:
      case ExceptionTypeInt.JankParsed: {
        return ExceptionCategory.JANK;
      }
      case ExceptionTypeInt.Hang: {
        return ExceptionCategory.HANG;
      }
      case 44: {
        return ExceptionCategory.METRICKIT_CRASH;
      }
      case ExceptionTypeInt.JankV2: {
        // 王者 ThreadMonitor 内部定制：JankV2（协议 145 / ES 45）走错误大类，不新建 ExceptionCategory
        return ExceptionCategory.ERROR;
      }
      case 46: {
        return ExceptionCategory.METRICKIT_CPU;
      }
      case 47: {
        return ExceptionCategory.METRICKIT_DISK;
      }
      case 60:
      case ExceptionTypeInt.AexitCrash: {
        return ExceptionCategory.AEXIT_CRASH;
      }
      case 61:
      case ExceptionTypeInt.AexitAnr: {
        return ExceptionCategory.AEXIT_ANR;
      }
      default: {
        return ExceptionCategory.ERROR;
      }
    }
  }

  function isAccess(chaosCategory) {
    return unify(chaosCategory) === ExceptionCategory.ACCESS;
  }

  function isCrash(chaosCategory) {
    return unify(chaosCategory) === ExceptionCategory.CRASH;
  }

  function isAnr(chaosCategory) {
    return unify(chaosCategory) === ExceptionCategory.ANR;
  }

  function isError(chaosCategory) {
    return unify(chaosCategory) === ExceptionCategory.ERROR;
  }

  function isOom(chaosCategory) {
    return unify(chaosCategory) === ExceptionCategory.OOM;
  }

  function isJank(chaosCategory) {
    return unify(chaosCategory) === ExceptionCategory.JANK;
  }

  function isHang(chaosCategory) {
    return unify(chaosCategory) === ExceptionCategory.HANG;
  }

  function isMetricKitCrash(chaosCategory) {
    return unify(chaosCategory) === ExceptionCategory.METRICKIT_CRASH;
  }

  function isMetricKitCpu(chaosCategory) {
    return unify(chaosCategory) === ExceptionCategory.METRICKIT_CPU;
  }

  function isMetricKitDisk(chaosCategory) {
    return unify(chaosCategory) === ExceptionCategory.METRICKIT_DISK;
  }

  function isAexitCrash(chaosCategory) {
    return unify(chaosCategory) === ExceptionCategory.AEXIT_CRASH;
  }

  function isAexitAnr(chaosCategory) {
    return unify(chaosCategory) === ExceptionCategory.AEXIT_ANR;
  }

  function isVital(chaosCategory) {
    return unify(chaosCategory) === ExceptionCategory.VITAL;
  }

  function getAvailableOptions(pid, enableJank, enableHang, enableMetricKit, isSuper = false) {
    const categories = [
      PlatformUtil.isCrashAvailable(pid) && ExceptionCategory.CRASH,
      PlatformUtil.isAnrAvailable(pid) && ExceptionCategory.ANR,
      ExceptionCategory.ERROR,
      enableJank && ExceptionCategory.JANK,
      enableHang && ExceptionCategory.HANG,
      enableMetricKit && ExceptionCategory.METRICKIT_CRASH,
      enableMetricKit && isSuper && ExceptionCategory.METRICKIT_CPU,
      enableMetricKit && isSuper && ExceptionCategory.METRICKIT_DISK,
    ].filter(x => x);
    return categories.map(x => ({ value: x, label: toI18n(x, pid) }));
  }

  return Object.freeze({
    unify,
    toI18n,
    fromExceptionType,
    fromExceptionTypeInt,
    isAccess,
    isCrash,
    isAnr,
    isError,
    isOom,
    isJank,
    isHang,
    isMetricKitCrash,
    isMetricKitCpu,
    isMetricKitDisk,
    isAexitCrash,
    isAexitAnr,
    isVital,
    getAvailableOptions,
  });
})();
